import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Site, Page, Component, PageLayout } from '../schemas/validation';
import { getPublicApiBaseUrl } from '../utils/publicApiBaseUrl';
import { parseSiteResponse, type NavigationItem } from '../hooks/parseWebsiteApiData';

export interface LandingPageContextValue {
  loading: boolean;
  /** True when no site has been designated as the Compete Pages landing page. */
  notConfigured: boolean;
  error: string | null;
  website: Site | null;
  pages: Page[];
  homePage: Page | null;
  homeComponents: Component[];
  navigation: NavigationItem[];
  theme: unknown;
  layout: PageLayout | null;
  showBranding: boolean;
}

const defaultValue: LandingPageContextValue = {
  loading: true,
  notConfigured: false,
  error: null,
  website: null,
  pages: [],
  homePage: null,
  homeComponents: [],
  navigation: [],
  theme: null,
  layout: null,
  showBranding: false,
};

const LandingPageContext = createContext<LandingPageContextValue>(defaultValue);

export const LandingPageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [value, setValue] = useState<LandingPageContextValue>(defaultValue);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchLandingPage = async () => {
      try {
        const apiBaseUrl = getPublicApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/landing-page`, {
          signal: controller.signal,
        });

        if (response.status === 404) {
          if (!cancelled) {
            setValue({ ...defaultValue, loading: false, notConfigured: true });
          }
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to load landing page (${response.status})`);
        }

        const body = await response.json();
        const data = body?.success ? body.data : body;
        if (cancelled) return;

        const parsed = parseSiteResponse(data);
        setValue({
          loading: false,
          notConfigured: false,
          error: null,
          ...parsed,
        });
      } catch (error) {
        if (cancelled || (error as Error).name === 'AbortError') return;
        setValue({
          ...defaultValue,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load landing page',
        });
      }
    };

    fetchLandingPage();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return <LandingPageContext.Provider value={value}>{children}</LandingPageContext.Provider>;
};

export const useLandingPageContext = () => useContext(LandingPageContext);
